import { useState, useEffect, useRef } from "react";
import { Search, Palette, Code2, FlaskConical, Rocket, HeartHandshake } from "lucide-react";

// ── Brand colors ───────────────────────────────────────────────────────────────
const BRAND_FROM = "#9333ea"; // purple-600
const BRAND_TO   = "#2563eb"; // blue-600
const BRAND_GRAD = `linear-gradient(135deg, ${BRAND_FROM}, ${BRAND_TO})`;

// ── useInView hook ─────────────────────────────────────────────────────────────
const useInView = (threshold = 0.15) => {
	const ref = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(false);
	useEffect(() => {
		const obs = new IntersectionObserver(
			([e]) => { if (e.isIntersecting) setVisible(true); },
			{ threshold }
		);
		if (ref.current) obs.observe(ref.current);
		return () => obs.disconnect();
	}, []);
	return [ref, visible] as const;
};

// ── Data ───────────────────────────────────────────────────────────────────────
const STEPS = [
	{
		icon: <Search size={22} strokeWidth={1.8} />,
		num: "01",
		title: "Discovery & Strategy",
		desc: "We deep-dive into your goals, users, and market. We define scope, KPIs, and a clear product roadmap before writing a single line of code.",
		tags: ["Kickoff Call", "Market Research", "Roadmap"],
		duration: "1–2 weeks",
	},
	{
		icon: <Palette size={22} strokeWidth={1.8} />,
		num: "02",
		title: "Design & Prototype",
		desc: "Interactive Figma prototypes let you experience the product before it's built. We iterate fast until every screen and flow feels exactly right.",
		tags: ["Wireframes", "Design System", "Hi-fi Mockups"],
		duration: "2–3 weeks",
	},
	{
		icon: <Code2 size={22} strokeWidth={1.8} />,
		num: "03",
		title: "Agile Development",
		desc: "Two-week sprints with continuous demos. Full visibility via shared boards and weekly calls. Your dedicated team ships fast and communicates clearly.",
		tags: ["Sprint Planning", "Daily Standups", "Live Demos"],
		duration: "8–16 weeks",
	},
	{
		icon: <FlaskConical size={22} strokeWidth={1.8} />,
		num: "04",
		title: "QA & Testing",
		desc: "Manual and automated testing across devices and edge cases. Zero-bug policy before we even consider shipping to production.",
		tags: ["Automated Tests", "Device Testing", "Performance Audit"],
		duration: "1–2 weeks",
	},
	{
		icon: <Rocket size={22} strokeWidth={1.8} />,
		num: "05",
		title: "Launch & Deploy",
		desc: "App Store submissions, DNS, SSL, CDN, and production deployment. We handle every unglamorous bit so your launch day is smooth.",
		tags: ["App Store Review", "Production Deploy", "DNS & CDN"],
		duration: "1 week",
	},
	{
		icon: <HeartHandshake size={22} strokeWidth={1.8} />,
		num: "06",
		title: "Growth & Support",
		desc: "Post-launch monitoring, performance tuning, feature iterations, and dedicated support. Long-term partners, not one-off vendors.",
		tags: ["24/7 Monitoring", "Feature Sprints", "Dedicated Slack"],
		duration: "Ongoing",
	},
];

// ── Types ──────────────────────────────────────────────────────────────────────
interface Step {
	icon: React.ReactNode;
	num: string;
	title: string;
	desc: string;
	tags: string[];
	duration: string;
}

interface TimelineStepProps {
	step: Step;
	index: number;
	isLast: boolean;
}

// ── StepCard ───────────────────────────────────────────────────────────────────
function StepCard({
	step,
	hovered,
	setHovered,
}: {
	step: Step;
	hovered: boolean;
	setHovered: (v: boolean) => void;
}) {
	return (
		<div
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className="relative max-w-sm w-full rounded-2xl border overflow-hidden cursor-default pb-6 pt-6 px-7"
			style={{
				background: hovered
					? "linear-gradient(135deg, rgba(147,51,234,0.12), rgba(37,99,235,0.08))"
					: "rgba(255,255,255,0.02)",
				borderColor: hovered ? "rgba(147,51,234,0.45)" : "rgba(255,255,255,0.07)",
				boxShadow: hovered
					? "0 20px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(147,51,234,0.2)"
					: "none",
				transition: "all 0.35s ease",
			}}
		>
			{/* Animated top accent line */}
			<div
				className="absolute top-0 left-0 right-0 h-[2px]"
				style={{
					background: BRAND_GRAD,
					opacity: hovered ? 1 : 0,
					transition: "opacity 0.3s ease",
				}}
			/>

			{/* Glow blob behind icon */}
			<div
				className="absolute -top-6 -left-6 w-24 h-24 rounded-full pointer-events-none"
				style={{
					background: "radial-gradient(circle, rgba(147,51,234,0.15), transparent 70%)",
					opacity: hovered ? 1 : 0,
					transition: "opacity 0.4s ease",
				}}
			/>

			{/* Step number */}
			<div className="flex items-center justify-between mb-4">
				<span
					className="font-black tracking-[0.22em] uppercase"
					style={{
						fontSize: "12px",
						background: BRAND_GRAD,
						WebkitBackgroundClip: "text",
						WebkitTextFillColor: "transparent",
					}}
				>
					Step {step.num}
				</span>
			</div>

			{/* Icon + title row */}
			<div className="flex items-center gap-3 mb-4">
				<div
					className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white"
					style={{
						background: BRAND_GRAD,
						boxShadow: hovered ? "0 6px 20px rgba(147,51,234,0.4)" : "0 4px 12px rgba(147,51,234,0.25)",
						transform: hovered ? "scale(1.1)" : "scale(1)",
						transition: "transform 0.3s ease, box-shadow 0.3s ease",
					}}
				>
					{step.icon}
				</div>
				<h3 className="text-white font-bold text-lg leading-snug">{step.title}</h3>
			</div>

			{/* Desc */}
			<p
				className="leading-relaxed mb-5"
				style={{ color: "rgba(255,255,255,0.65)", fontSize: "15px" }}
			>
				{step.desc}
			</p>

			{/* Tags */}
			<div className="flex flex-wrap gap-2">
				{step.tags.map((tag) => (
					<span
						key={tag}
						className="font-semibold px-3 py-1 rounded-lg border"
						style={{
							fontSize: "13px",
							color: hovered ? "rgba(167,139,250,1)" : "rgba(255,255,255,0.55)",
							borderColor: hovered ? "rgba(147,51,234,0.25)" : "rgba(255,255,255,0.1)",
							background: hovered ? "rgba(147,51,234,0.08)" : "rgba(255,255,255,0.04)",
							transition: "all 0.3s ease",
						}}
					>
						{tag}
					</span>
				))}
			</div>
		</div>
	);
}

// ── Desktop zigzag step ────────────────────────────────────────────────────────
function TimelineStep({ step, index, isLast }: TimelineStepProps) {
	const [ref, visible] = useInView();
	const [hovered, setHovered] = useState(false);
	const isEven = index % 2 === 0;

	return (
		<div
			ref={ref}
			style={{ transitionDelay: `${index * 130}ms` }}
			className={`relative grid grid-cols-[1fr_auto_1fr] transition-all duration-700
				${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
		>
			{/* Left content (even steps) */}
			<div className={`flex justify-end pr-10 py-3 ${isEven ? "" : "invisible"}`}>
				{isEven && <StepCard step={step} hovered={hovered} setHovered={setHovered} />}
			</div>

			{/* ── Center spine ── */}
			<div className="flex flex-col items-center">

				{/* Node */}
				<div
					onMouseEnter={() => setHovered(true)}
					onMouseLeave={() => setHovered(false)}
					className="relative flex items-center justify-center w-14 h-14 rounded-full cursor-pointer z-10 shrink-0"
					style={{
						background: hovered ? BRAND_GRAD : "#1e1e1e",
						border: `2px solid ${hovered ? BRAND_FROM : "rgba(255,255,255,0.12)"}`,
						boxShadow: hovered
							? `0 0 0 6px rgba(147,51,234,0.12), 0 0 28px rgba(147,51,234,0.45)`
							: "none",
						transition: "all 0.3s ease",
					}}
				>
					<span
						style={{
							color: hovered ? "white" : BRAND_FROM,
							transition: "color 0.3s ease",
						}}
					>
						{step.icon}
					</span>

					<div
						className="absolute inset-0 rounded-full"
						style={{
							border: `2px solid ${BRAND_FROM}`,
							opacity: hovered ? 0 : 0.18,
							transform: hovered ? "scale(1.6)" : "scale(1.2)",
							transition: "transform 0.6s ease, opacity 0.4s ease",
						}}
					/>

					{hovered && (
						<div
							className="absolute inset-0 rounded-full animate-ping"
							style={{
								background: BRAND_GRAD,
								opacity: 0.2,
							}}
						/>
					)}
				</div>

				{/* Spine line */}
				{!isLast && (
					<div
						className="w-px flex-1 mt-1 min-h-[3.5rem]"
						style={{
							background: visible
								? `linear-gradient(to bottom, rgba(147,51,234,0.35), rgba(37,99,235,0.15), rgba(255,255,255,0.04))`
								: "rgba(255,255,255,0.04)",
							transition: "background 0.6s ease",
						}}
					/>
				)}
			</div>

			{/* Right content (odd steps) */}
			<div className={`flex justify-start pl-10 py-3 ${!isEven ? "" : "invisible"}`}>
				{!isEven && <StepCard step={step} hovered={hovered} setHovered={setHovered} />}
			</div>
		</div>
	);
}

// ── Mobile step ────────────────────────────────────────────────────────────────
function MobileStep({ step, index, isLast }: TimelineStepProps) {
	const [ref, visible] = useInView();

	return (
		<div
			ref={ref}
			style={{ transitionDelay: `${index * 100}ms` }}
			className={`flex gap-4 transition-all duration-500
				${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
		>
			{/* Spine */}
			<div className="flex flex-col items-center shrink-0">
				<div
					className="w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0"
					style={{
						background: BRAND_GRAD,
						boxShadow: "0 0 18px rgba(147,51,234,0.4)",
					}}
				>
					{step.icon}
				</div>
				{!isLast && (
					<div
						className="w-px flex-1 mt-1 min-h-[2rem]"
						style={{
							background: "linear-gradient(to bottom, rgba(147,51,234,0.3), rgba(37,99,235,0.1))",
						}}
					/>
				)}
			</div>

			{/* Card */}
			<div
				className="flex-1 rounded-xl border p-5 mb-5"
				style={{
					background: "linear-gradient(135deg, rgba(147,51,234,0.08), rgba(37,99,235,0.04))",
					borderColor: "rgba(147,51,234,0.2)",
				}}
			>
				<div className="flex items-center justify-between mb-2.5">
					<span
						className="font-black tracking-[0.18em] uppercase"
						style={{
							fontSize: "12px",
							background: BRAND_GRAD,
							WebkitBackgroundClip: "text",
							WebkitTextFillColor: "transparent",
						}}
					>
						Step {step.num}
					</span>
				</div>
				<h3 className="text-white font-bold text-base mb-2">{step.title}</h3>

				<p className="leading-relaxed mb-3" style={{ color: "rgba(255,255,255,0.65)", fontSize: "14px" }}>
					{step.desc}
				</p>

				{/* Tags */}
				<div className="flex flex-wrap gap-1.5">
					{step.tags.map((tag) => (
						<span
							key={tag}
							className="font-semibold px-2.5 py-1 rounded border"
							style={{
								fontSize: "11px",
								color: "rgba(167,139,250,0.9)",
								borderColor: "rgba(147,51,234,0.25)",
								background: "rgba(147,51,234,0.08)",
							}}
						>
							{tag}
						</span>
					))}
				</div>
			</div>
		</div>
	);
}

// ── Main export ────────────────────────────────────────────────────────────────
export default function ProcessSection() {
	const [ref, visible] = useInView();

	return (
		<section id="process" className="py-20 px-6 relative overflow-hidden">

			{/* Ambient background glows */}
			<div
				className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
				style={{
					background: "radial-gradient(circle, rgba(147,51,234,0.05) 0%, transparent 65%)",
				}}
			/>
			<div
				className="absolute bottom-1/4 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
				style={{
					background: "radial-gradient(circle, rgba(37,99,235,0.05) 0%, transparent 65%)",
				}}
			/>

			<div className="max-w-5xl mx-auto relative z-10">

				{/* ── Header ── */}
				<div
					ref={ref}
					className={`text-center mb-20 transition-all duration-700
						${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
				>
					<h2 className="text-4xl md:text-5xl font-bold text-white leading-[0.95] tracking-tight mb-5">
						Our Development Journey
					</h2>
					<p className="text-gray-300 md:text-lg">
						A transparent, step-by-step process — from the first call to launch and beyond.
					</p>
				</div>

				{/* ── Desktop: zigzag timeline ── */}
				<div className="hidden md:block">
					{STEPS.map((step, i) => (
						<TimelineStep
							key={step.num}
							step={step}
							index={i}
							isLast={i === STEPS.length - 1}
						/>
					))}
				</div>

				{/* ── Mobile: left-aligned timeline ── */}
				<div className="md:hidden">
					{STEPS.map((step, i) => (
						<MobileStep
							key={step.num}
							step={step}
							index={i}
							isLast={i === STEPS.length - 1}
						/>
					))}
				</div>

			</div>
		</section>
	);
}